import { useState, useCallback } from 'react';
// @mui
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Card, Container, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
// components
import Iconify from '../../../components/iconify';
import { MY_CLIENT_LIST_TABLE_DEFAULT_PROPS } from '../constants';
import { ActionsPopover, ClientFormModal, DeleteModal, Header } from '.';
import Loading from '../../../layouts/loading';

// styles
import 'react-toastify/dist/ReactToastify.css';
import { createClient, deleteClient, getClients, updateClient } from '../../../services/clientService';
import { DEFAULT_TABLE_PAGE_LIMIT } from '../../../Constants/table.constants';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import TableWrapper from '../../../common/TableWrapper';

// ----------------------------------------------------------------------

function ClientTable() {
  const [openActions, setOpenActions] = useState(null);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [action, setAction] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_TABLE_PAGE_LIMIT,
  });

  const boldHeader = (color) => (table) => <b style={{ color }}>{table?.column?.columnDef?.header}</b>;

  const {
    data: { count = 0, rows = [] } = {},
    isError,
    isRefetching,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting],
    queryFn: async () => {
      try {
        const response = await getClients({
          start: pagination.pageIndex * pagination.pageSize,
          pageSize: pagination.pageSize,
          filters: JSON.stringify(columnFilters ?? []),
          globalFilter,
          sorting: JSON.stringify(sorting ?? []),
        });
        return response?.data?.data;
      } catch (error) {
        toast.error('An error occurred while fetching the task list. Please try again later.');
        return { count: 0, rows: [] };
      }
    },
    placeholderData: keepPreviousData,
  });

  const columns = [
    {
      accessorKey: 'clientName',
      id: 'clientName',
      header: 'Client',
      size: 650,
      Header: boldHeader('#454F5B'),
      Cell: ({ cell }) => <span style={{ textTransform: 'capitalize' }}>{cell.getValue()}</span>,
    },
    {
      id: 'actions',
      Header: 'Actions',
      size: 30,
      Cell: ({ row }) => (
        <IconButton size="large" color="inherit" onClick={(e) => handleOpenMenu(e, row.original)}>
          <Iconify icon={'eva:more-vertical-fill'} />
        </IconButton>
      ),
    },
  ];

  const table = useMaterialReactTable({
    columns,
    data: rows,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    muiToolbarAlertBannerProps: isError ? { color: 'error', children: 'Error loading data' } : undefined,
    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      showSkeletons: isRefetching,
      showAlertBanner: isError,
    },
    rowCount: count,
    muiTableBodyRowProps: ({ row }) => ({
      sx: { backgroundColor: '#fff' },
    }),
    ...MY_CLIENT_LIST_TABLE_DEFAULT_PROPS,
  });

  const handleCreateClick = () => {
    setAction('create');
    setSelectedRow(null);
    setOpenFormModal(true);
  };

  const handleFormModalClose = useCallback(() => {
    setOpenFormModal(false);
  }, []);

  const handleOpenMenu = (event, row) => {
    setOpenActions(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setOpenActions(null);
  };

  const handleDeleteClick = () => {
    setOpenActions(null);
    setOpenDelete(true);
  };

  const handleDeleteModalClose = () => setOpenDelete(false);

  const handleEditClick = () => {
    setOpenActions(null);
    setAction('update');
    setOpenFormModal(true);
  };

  const handleDelete = async () => {
    try {
      const response = await deleteClient(selectedRow?.id);
      toast.success(response?.data?.message);
      setOpenDelete(false);
      refetch();
    } catch (error) {
      toast.error('Sorry, an error occurred while deleting. Please try again later.');
    }
  };

  const handleFormModalSubmit = useCallback(
    async (values) => {
      try {
        values.clientName = values?.clientName?.trim();
        let response;
        if (action === 'create') {
          response = await createClient(values);
        } else if (action === 'update') {
          response = await updateClient(selectedRow.id, values);
        }
        refetch();
        toast.success(response?.data?.message);
        setOpenFormModal(false);
      } catch (error) {
        toast.error(`${values?.clientName} already exists`);
      }
    },
    [action, selectedRow?.id, refetch]
  );

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'Clients',
      path: '',
    },
  ];

  return (
    <>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="4xl">
        <Header onCreateClick={handleCreateClick} />
        <Loading open={isLoading} />

        <Card>
          <TableWrapper>
            <MaterialReactTable table={table} />
          </TableWrapper>
        </Card>
      </Container>

      <ActionsPopover
        open={openActions}
        onClose={handleCloseMenu}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
      />
      {openFormModal && (
        <ClientFormModal
          open
          onClose={handleFormModalClose}
          selectedRow={selectedRow}
          onSubmit={handleFormModalSubmit}
          action={action}
        />
      )}
      <DeleteModal open={openDelete} onClose={handleDeleteModalClose} onDelete={handleDelete} />
    </>
  );
}

export default ClientTable;
